import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Loader2, KeyRound, CheckCircle2 } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { setAccessToken } from '@adthlete/shared-frontend'
import type { AppDispatch } from '@/store'
import { fetchCurrentUser } from '@/redux/userSlice'
import athleteApi from '@/lib/api'

interface ClaimInfo {
  athleteName: string
  athleteAvatar: string | null
  managerName: string
  email: string
}

const ClaimAccount = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!token) return
    athleteApi
      .get(`/public/claim-account/${token}`)
      .then((res) => setClaimInfo(res.data))
      .catch((err) => {
        setError(err.response?.data?.error || 'This invitation is invalid or has expired.')
      })
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      const res = await athleteApi.post(`/public/claim-account/${token}`, { password })
      // Store the access token in memory (refresh token is set as an HTTP-only cookie) and
      // load the profile so route guards see an authenticated session.
      setAccessToken(res.data.token)
      await dispatch(fetchCurrentUser())
      setSuccess(true)
      setTimeout(() => navigate('/'), 1800)
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-[#BCF565]" />
      </div>
    )
  }

  if (error || !claimInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md bg-card border-foreground/10 text-foreground">
          <CardHeader>
            <CardTitle className="text-red-600">Invitation Error</CardTitle>
            <CardDescription className="text-gray-600">
              {error || 'Invitation not found or invalid.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-muted hover:bg-foreground/20 text-foreground"
              onClick={() => navigate('/signin')}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md bg-card border-foreground/10 text-foreground text-center">
          <CardContent className="pt-10 pb-10 space-y-4">
            <CheckCircle2 className="h-14 w-14 text-[#BCF565] mx-auto" />
            <p className="text-xl font-semibold">You're all set!</p>
            <p className="text-gray-600 text-sm">Taking you to your dashboard…</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const initials = claimInfo.athleteName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#BCF565]/5 rounded-full blur-3xl pointer-events-none" />

      <Card className="w-full max-w-lg bg-card/90 backdrop-blur-xs border-foreground/10 text-foreground relative z-10">
        <CardHeader className="text-center pb-8 border-b border-foreground/5">
          <div className="mx-auto bg-[#BCF565]/10 p-3 rounded-full w-fit mb-4">
            <KeyRound className="h-8 w-8 text-[#BCF565]" />
          </div>
          <CardTitle className="text-2xl font-bold">Claim Your Account</CardTitle>
          <CardDescription className="text-gray-600 max-w-xs mx-auto mt-2">
            {claimInfo.managerName} has set up a profile for you. Set a password to take ownership.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-8 space-y-8">
          {/* Athlete identity */}
          <div className="flex flex-col items-center space-y-3">
            <Avatar className="h-20 w-20 border-2 border-[#BCF565]/20">
              {claimInfo.athleteAvatar && <AvatarImage src={claimInfo.athleteAvatar} />}
              <AvatarFallback className="bg-[#BCF565]/10 text-[#BCF565] text-xl">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="text-center">
              <p className="text-lg font-semibold">{claimInfo.athleteName}</p>
              <p className="text-sm text-gray-600">{claimInfo.email}</p>
            </div>
          </div>

          {/* Password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">
                Choose a password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="bg-card border-foreground/10 text-foreground placeholder:text-gray-400 focus:border-[#BCF565]/50"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-gray-700">
                Confirm password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                className="bg-card border-foreground/10 text-foreground placeholder:text-gray-400 focus:border-[#BCF565]/50"
                required
              />
            </div>

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <Button
              type="submit"
              className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055] h-12 font-semibold mt-2"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Claim Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default ClaimAccount
